import { useState, useEffect, useCallback } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import { Moment } from "moment";

import {
  Badge,
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { purple } from "@mui/material/colors";
import {
  IconHourglassHigh,
  IconMessage,
  IconMessageFilled,
} from "@tabler/icons-react";
import { LuBicepsFlexed } from "react-icons/lu";

import CurrentSession from "data/userSession";
import LastTugged from "features/user/LastTugged";
import TimeUtils from "utils/time";
import Streak from "features/user/Streak";
import useCustomSnackbar from "hooks/customSnackbar";
import useCustomNavigation from "hooks/customNavigation";
import { getStreakMessage } from "features/tug/streak";
import { ChatService } from "data/firestore/chat";
import AnalyticsService from "data/firestore/analytics";
import CustomChip from "components/Chip";

export interface HeaderContentProps {
  userId?: string;
}

const HeaderContent = ({ userId }: HeaderContentProps) => {
  const theme = useTheme();
  const navigate = useCustomNavigation();
  const { enqueueSnackbar } = useCustomSnackbar();
  // On small screens condense "Xh MMm" -> "Xh" so the header (avatar + last
  // tugged + 4-digit streak + unread badge) fits without overflowing.
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [lastTugged, setLastTugged] = useState<Moment | null>(null);
  const [streak, setStreak] = useState(0);
  const [streakInDanger, setStreakInDanger] = useState(false);
  const [untilMidnight, setUntilMidnight] = useState<number | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isIncognito, setIsIncognito] = useState(false);

  function updateData() {
    setStreak(CurrentSession.user.streak);
    setStreakInDanger(
      CurrentSession.user.streakInDanger && !CurrentSession.user.streakDisabled
    );
    setLastTugged(CurrentSession.user.lastTugged);
    setInProgress(CurrentSession.user.activeRecently);
  }

  useEffect(() => {
    CurrentSession.load().then((user) => {
      setIsIncognito(user.incognito ?? false);
      updateData();
    });
  }, []);

  // User data is Supabase-backed now (no Firestore doc to watch). The tug page and
  // any save dispatch these events; refresh streak/last-tugged from Supabase and
  // re-render on them so the countdown vs. "Active" toggle stays current.
  useEffect(() => {
    const handler = async () => {
      await CurrentSession.user.refreshStreak();
      updateData();
      setIsIncognito(CurrentSession.user.incognito ?? false);
    };
    window.addEventListener("userUpdated", handler);
    window.addEventListener("tugSessionChanged", handler);
    return () => {
      window.removeEventListener("userUpdated", handler);
      window.removeEventListener("tugSessionChanged", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to unread chat count
  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    const unsubscribe = ChatService.listenToUnreadCount(
      userId,
      (count: number) => {
        setUnreadCount(count);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Automatically update the countdown until midnight
  useEffect(() => {
    const interval = setInterval(() => {
      setUntilMidnight(TimeUtils.untilMidnight());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Automatically reset data at midnight
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function scheduleMidnightUpdate() {
      setUntilMidnight(TimeUtils.untilMidnight());

      timeoutId = setTimeout(() => {
        updateData();
        scheduleMidnightUpdate();
      }, TimeUtils.untilMidnight());
    }

    scheduleMidnightUpdate();

    return () => clearTimeout(timeoutId);
  }, []);

  // Handle streak button press. Stable identity (useCallback) so the memoized
  // Streak child isn't forced to re-render every second when the header ticks.
  const handleStreakPress = useCallback(
    (currentStreak: number, isInDanger: boolean) => {
      if (isInDanger) {
        enqueueSnackbar("Get a quick tug session in to save your streak!", {
          variant: "warning",
          autoHideDuration: 4000,
        });
      } else if (currentStreak > 0) {
        setShowConfetti(true);
        enqueueSnackbar(getStreakMessage(currentStreak), {
          variant: "streak",
          autoHideDuration: 5000,
        });
        setTimeout(() => setShowConfetti(false), 3000);
      }
    },
    [enqueueSnackbar]
  );

  // Handle LastTugged click
  const handleLastTuggedPress = () => {
    enqueueSnackbar("Time since you last tugged", {
      variant: "info",
      autoHideDuration: 3000,
    });
  };

  // Handle countdown click
  const handleCountdownPress = () => {
    enqueueSnackbar(
      "Time until your streak breaks! Sneak in a quick 2-min tug?",
      {
        variant: "warning",
        autoHideDuration: 4000,
      }
    );
  };

  // Handle chat icon click
  const handleChatPress = () => {
    AnalyticsService.event("click_chat_icon_in_header");
    navigate("chats");
  };

  // Chat icon component
  const hasUnread = unreadCount > 0;
  const MessageIcon = hasUnread ? IconMessageFilled : IconMessage;

  const chatIcon = (
    <Badge
      badgeContent={unreadCount}
      color="error"
      max={99}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: "0.7rem",
          height: 18,
          minWidth: 18,
        },
      }}
    >
      <IconButton
        onClick={handleChatPress}
        sx={{
          transition: "all .2s ease-in-out",
          background: hasUnread ? purple[500] : "white",
          color: hasUnread ? purple[50] : purple[700],
          "&:hover": {
            background: hasUnread ? purple[700] : purple[50],
            color: hasUnread ? purple[50] : purple[800],
          },
          height: 40,
          width: 40,
        }}
      >
        <MessageIcon stroke={1.5} size={22} />
      </IconButton>
    </Badge>
  );

  // No streak or lastTugged - show encouragement chip + chat
  if (streak <= 0 || !lastTugged) {
    return (
      <Grid container alignItems="center" justifyContent="flex-end" spacing={1}>
        <Grid item>
          <Tooltip title="Let's get tugging!" arrow>
            <CustomChip
              color="info"
              icon={<LuBicepsFlexed />}
              label="You got this"
              sx={{
                fontSize: "1.5em",
                "& span": { overflow: "visible" },
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              }}
            />
          </Tooltip>
        </Grid>
        {!isIncognito && <Grid item>{chatIcon}</Grid>}
      </Grid>
    );
  }

  // Full content: LastTugged/Countdown + Streak + Chat
  return (
    <Grid
      container
      alignItems="center"
      sx={{
        justifyContent: { xs: "space-evenly", sm: "flex-end" },
        minWidth: 0,
        maxWidth: "100%",
      }}
      wrap="nowrap"
    >
      {showConfetti && <ConfettiExplosion particleCount={400} />}
      <Grid item sx={{ mr: { xs: 0, sm: 1 } }}>
        {streakInDanger ? (
          <Tooltip title="Countdown until end of streak :(" arrow>
            <Chip
              onClick={handleCountdownPress}
              icon={<IconHourglassHigh />}
              color={
                untilMidnight !== null && untilMidnight < 21600000
                  ? "error"
                  : "warning"
              }
              label={TimeUtils.formatTimer(untilMidnight ?? 0)}
              sx={{
                cursor: "pointer",
                fontSize: "1.5em",
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: "bold",
                ml: 0.5,
              }}
            />
          </Tooltip>
        ) : (
          <Box onClick={handleLastTuggedPress} sx={{ cursor: "pointer" }}>
            <LastTugged
              date={lastTugged}
              style={{ fontSize: "1.25em" }}
              isCurrentUser={true}
              inProgress={inProgress}
              maxUnits={isSmall ? 1 : 2}
            />
          </Box>
        )}
      </Grid>
      <Grid
        item
        sx={{
          mr: { xs: 0, sm: 1 },
          display: { xs: streakInDanger ? "none" : "block", sm: "block" },
        }}
      >
        <Streak
          isCurrentUser={true}
          variant="full"
          clickable={true}
          onPress={handleStreakPress}
        />
      </Grid>
      {!isIncognito && <Grid item>{chatIcon}</Grid>}
    </Grid>
  );
};

export default HeaderContent;
